// Hero — time-of-day background video over an always-rendered static
// cover (the cover IS the placeholder: video mounts on top only when the
// device/network can afford it), logo, tenure counters, CTA row. Counters
// come from src/lib/index-time.ts (Asia/Shanghai, same math as the legacy
// page).
import { useEffect, useState } from "react";
import { indexTimeValues } from "../../lib/index-time";
import { smoothScrollToHash } from "../../lib/smooth-scroll";

interface HeroProps {
  readonly onOpenDownload: () => void;
}

interface ConnectionLike {
  readonly saveData?: boolean;
  readonly effectiveType?: string;
}

// Format probe for source ORDERING only. canPlayType is advisory (some UA
// families say "probably" for VP9 then reject the file mid-selection while
// h264 plays fine) — so both formats are always offered, the probe merely
// prefers the ~1MB webm over the ~2.8MB mp4 when it claims support.
function probePrefersWebm(): boolean {
  return (
    document.createElement("video").canPlayType('video/webm; codecs="vp9"') !== ""
  );
}

// Video eligibility: wide viewport, no reduced-motion, and a connection
// that isn't metered/slow (Save-Data or 2g-class). The video is a
// progressive enhancement layered over the cover image, never blocking it.
function useVideoEligibility(): { ok: boolean; webmFirst: boolean } {
  const [state, setState] = useState({ ok: false, webmFirst: true });
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const narrow = window.matchMedia("(max-width: 767px)");
    const conn = (navigator as { connection?: ConnectionLike }).connection;
    const webmFirst = probePrefersWebm();
    const evalNow = () => {
      const netOK =
        !conn ||
        (!conn.saveData &&
          conn.effectiveType !== "slow-2g" &&
          conn.effectiveType !== "2g");
      setState({ ok: !reduced.matches && !narrow.matches && netOK, webmFirst });
    };
    evalNow();
    reduced.addEventListener("change", evalNow);
    narrow.addEventListener("change", evalNow);
    return () => {
      reduced.removeEventListener("change", evalNow);
      narrow.removeEventListener("change", evalNow);
    };
  }, []);
  return state;
}

export function Hero({ onOpenDownload }: HeroProps) {
  const { season, diffInYears, diffInDays } = indexTimeValues(Date.now());
  const video = useVideoEligibility();

  return (
    <header className="wv-h2-hero" id="top">
      <div className="wv-h2-hero-media">
        {/* Static cover always renders (LCP + weak-network fallback); the
            video layers over it only when eligible. */}
        <img src={`/static/videos/${season}.jpg`} alt="" />
        {video.ok && (
          // NO onError anywhere: a native repro in the target UA proved that
          // any React onError on the <video> receives bubbled <source>
          // errors and unmounting on them kills the native fall-through to
          // the next candidate. Handler-free sources select correctly
          // (webm error → mp4 plays). In the unreachable all-dead case the
          // element just sits on its last frame — acceptable.
          <video autoPlay muted loop playsInline>
            {/* webm (~1MB) preferred when the probe claims VP9, mp4 always
                offered, webm repeated last so a webm-only UA still has a
                candidate after the mp4 errors. */}
            {video.webmFirst && (
              <source
                src={`/static/videos/${season}.webm`}
                type='video/webm; codecs="vp9"'
              />
            )}
            <source
              src={`/static/videos/${season}.mp4`}
              type="video/mp4"
            />
            {!video.webmFirst && (
              <source
                src={`/static/videos/${season}.webm`}
                type='video/webm; codecs="vp9"'
              />
            )}
          </video>
        )}
      </div>
      <div className="wv-h2-hero-overlay"></div>

      <div className="wv-h2-hero-body">
        <img className="wv-h2-hero-logo" src="/static/images/index/logo-lg.png" alt="随便MC" />
        <h1>
          <span className="thin">{diffInYears}年老牌优秀公益服</span>
          <br />
          欢迎来到随便MC
        </h1>
        <p>
          随便MC~是资格最老的我的世界公益服，已经坚持为广大Minecraft爱好者提供了
          {diffInDays} 天稳定的免费服务器平台。
        </p>
        <p>欢迎加入我们随便MC~大家庭！</p>

        <div className="wv-h2-hero-cta">
          <button className="wv-h2-btn wv-h2-btn--primary wv-h2-btn--lg" onClick={onOpenDownload}>
            连接服务器or下载游戏
          </button>
          <a className="wv-h2-btn wv-h2-btn--sky wv-h2-btn--lg" href="/acidisland">
            海岛服
          </a>
          <a className="wv-h2-btn wv-h2-btn--info wv-h2-btn--lg wv-h2-cta-login" href="/login">
            登录
          </a>
          <a className="wv-h2-btn wv-h2-btn--info wv-h2-btn--lg wv-h2-cta-user" href="/user/">
            用户中心
          </a>
          <a
            className="wv-h2-btn wv-h2-btn--ghost wv-h2-btn--lg"
            href="#about"
            onClick={(e) => smoothScrollToHash(e, "#about")}
          >
            了解更多
          </a>
        </div>
      </div>

      <a
        className="wv-h2-hero-down"
        href="#about"
        aria-label="向下滚动"
        onClick={(e) => smoothScrollToHash(e, "#about")}
      >
        ▼
      </a>
    </header>
  );
}
